import {
  ContractInvoiceType,
  ContractStatus,
} from "@boark/shared/types/contract";
import { useNavigate, useParams } from "react-router-dom";

import { FileList } from "../../components/file-list";
import { H1 } from "../../components/h1";
import { Modal } from "../../components/modal";
import { useGetContract } from "../../queries/contract";
import { useLocale } from "../../stores/locale";
import { formatDate } from "../../utils/date";
import { navigateBack } from "../../utils/navigate-back";

export function Contract() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { locale: t } = useLocale();

  if (!id) {
    throw new Error("Contract ID is required");
  }

  const { data: contract } = useGetContract(id);

  const closeModal = () => {
    navigateBack(navigate, "/admin/contract/list");
  };

  function invoiceTypeLabel(invoiceType: ContractInvoiceType) {
    switch (invoiceType) {
      case ContractInvoiceType.MONTHLY:
        return t.contract.invoiceTypeMonthly;
      case ContractInvoiceType.QUARTERLY:
        return t.contract.invoiceTypeQuarterly;
      case ContractInvoiceType.SEMI_ANNUALLY:
        return t.contract.invoiceTypeSemiAnnually;
      case ContractInvoiceType.ANNUALLY:
        return t.contract.invoiceTypeAnnually;
    }
  }

  return (
    <Modal open onClose={closeModal}>
      {contract && (
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <H1>{contract.title}</H1>
            <Field label={t.contract.status}>
              {contract.status === ContractStatus.ACTIVE
                ? t.contract.statusActive
                : t.contract.statusInactive}
            </Field>
            <Field label={t.contract.cost}>
              {contract.cost !== undefined
                ? contract.cost.toString()
                : t.contract.emptyCell}
            </Field>
            <Field label={t.contract.invoiceType}>
              {contract.invoiceType
                ? invoiceTypeLabel(contract.invoiceType)
                : t.contract.emptyCell}
            </Field>
            <Field label={t.contract.counterParty}>
              {contract.counterParty
                ? `${contract.counterParty.name || t.contract.emptyCell} (${contract.counterParty.corporateRegistrationNumber})`
                : t.contract.emptyCell}
            </Field>
            <Field label={t.contract.fromDate}>
              {formatDate(contract.fromDate)}
            </Field>
            <Field label={t.contract.toDate}>
              {contract.toDate
                ? formatDate(contract.toDate)
                : t.contract.emptyCell}
            </Field>
            <Field label={t.contract.tags}>
              {contract.tags.length > 0
                ? contract.tags.join(", ")
                : t.contract.emptyCell}
            </Field>
            <Field label={t.contract.description}>
              {contract.description || t.contract.emptyCell}
            </Field>
          </div>
          <FileList rootPath={`contract/${id}`} />
        </div>
      )}
    </Modal>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-2">
      <span className="font-bold">{label}:</span>
      <span>{children}</span>
    </div>
  );
}
